import React, { useState, useEffect, useRef } from 'react';
import { API_URL } from '..';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

const AddServiceLayer = () => {
    const [formValues, setFormValues] = useState({
        name: '',
        price: 0,
        description: ''
    });
    const [message, setMessage] = useState({
        status: false,
        reason: '',
        desc: ''
    });
    const quillRef = useRef(null);
    const quillInstance = useRef(null);

    // Initialize Quill editor
    useEffect(() => {
        if (quillRef.current && !quillInstance.current) {
            quillInstance.current = new Quill(quillRef.current, {
                theme: 'snow',
                modules: {
                    toolbar: [
                        ['bold', 'italic', 'underline'],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        [{ align: '' }, { align: 'center' }, { align: 'right' }],
                    ],
                },
                placeholder: 'Enter service description',
            });

            // Set text direction to LTR and language to English
            quillInstance.current.root.setAttribute('dir', 'ltr');
            quillInstance.current.root.setAttribute('lang', 'en');

            // Handle text changes and focus
            quillInstance.current.on('text-change', () => {
                const content = quillInstance.current.root.innerHTML;
                setFormValues((prev) => ({ ...prev, description: content }));
                quillInstance.current.focus();
            });

            // Focus the editor on initialization
            quillInstance.current.focus();
        }

        // Cleanup on unmount
        return () => {
            if (quillInstance.current) {
                quillInstance.current.off('text-change');
                quillInstance.current = null;
            }
        };
    }, []);

    // Update Quill content when description changes
    useEffect(() => {
        if (quillInstance.current && formValues.description) {
            if (quillInstance.current.root.innerHTML !== formValues.description) {
                quillInstance.current.root.innerHTML = formValues.description;
                quillInstance.current.focus();
            }
        }
    }, [formValues.description]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user) {
            setMessage({ status: true, reason: 'error', desc: 'User not authenticated. Please sign in.' });
            return;
        }

        // Validation
        if (!formValues.name) {
            setMessage({ status: true, reason: 'error', desc: 'Service name is required' });
            return;
        }
        if (!formValues.price || formValues.price <= 0) {
            setMessage({ status: true, reason: 'error', desc: 'Price must be greater than 0' });
            return;
        }
        if (!formValues.description || formValues.description === '<p><br></p>') {
            setMessage({ status: true, reason: 'error', desc: 'Description is required' });
            return;
        }

        try {
            let headersList = {
                "Accept": "*/*",
                "Content-Type": "application/json"
            };
            let response = await fetch(`${API_URL}/api/service/create`, {
                method: "POST",
                headers: headersList,
                body: JSON.stringify(formValues)
            });
            const data = await response.json();

            if (response.status === 201 || response.status === 200) {
                setMessage({ status: true, reason: 'success', desc: 'Service added successfully' });
                setFormValues({
                    name: '',
                    price: 0,
                    description: ''
                });
                if (quillInstance.current) {
                    quillInstance.current.root.innerHTML = '';
                    quillInstance.current.focus();
                }
            } else {
                throw new Error(data.message || 'Failed to add service');
            }
        } catch (error) {
            setMessage({ status: true, reason: 'error', desc: `Failed to add service: ${error.message}` });
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-body p-24">
                <style>
                    {`
                        .ql-container {
                            font-family: Arial, sans-serif;
                            font-size: 14px;
                            min-height: 150px;
                            border: 1px solid #ced4da;
                            border-radius: 8px;
                            direction: ltr;
                        }
                        .ql-container.is-invalid {
                            border-color: #dc3545;
                        }
                        .ql-editor {
                            min-height: 120px;
                            padding: 10px;
                        }
                        .ql-toolbar {
                            border-bottom: 1px solid #ced4da;
                            border-top-left-radius: 8px;
                            border-top-right-radius: 8px;
                        }
                        .ql-editor:focus {
                            outline: none;
                            box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
                        }
                    `}
                </style>
                <div className="row justify-content-center">
                    <div className="col-xxl-6 col-xl-8 col-lg-10">
                        <div className="card border">
                            <div className="card-body">
                                {/* Message Alert */}
                                {message.status && (
                                    <div className={`alert alert-${message.reason === 'error' ? 'danger' : 'success'} alert-dismissible fade show mb-20`} role="alert">
                                        {message.desc}
                                        <button
                                            type="button"
                                            className="btn-close"
                                            onClick={() => setMessage({ ...message, status: false })}
                                            aria-label="Close"
                                        ></button>
                                    </div>
                                )}

                                {/* Service Form */}
                                <form onSubmit={handleSubmit}>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="serviceName"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Service Name <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control radius-8"
                                            id="serviceName"
                                            value={formValues.name}
                                            onChange={(e) => setFormValues((prev) => ({ ...prev, name: e.target.value }))}
                                            placeholder="Service Name...."
                                        />
                                    </div>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="servicePrice"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Price <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control radius-8"
                                            id="servicePrice"
                                            value={formValues.price}
                                            onChange={(e) => setFormValues((prev) => ({ ...prev, price: e.target.value }))}
                                            placeholder="0.00"
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="serviceDescription"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Description <span className="text-danger-600">*</span>
                                        </label>
                                        <div
                                            ref={quillRef}
                                            className={`ql-container ${message.reason === 'error' && (!formValues.description || formValues.description === '<p><br></p>') ? 'is-invalid' : ''}`}
                                        />
                                        <div className="form-text">Provide a detailed description of the service (supports bold, bullet points, etc.).</div>
                                        {message.reason === 'error' && (!formValues.description || formValues.description === '<p><br></p>') && (
                                            <div style={{ color: '#dc3545', fontSize: '0.875em' }}>Description is required</div>
                                        )}
                                    </div>
                                    <div className="d-flex align-items-center justify-content-center gap-3">
                                        <button
                                            type="submit"
                                            className="btn btn-primary border border-primary-400 text-md px-56 py-12 radius-8"
                                        >
                                            Submit
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddServiceLayer;